import { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import VehicleCategoryForm from "./_components/VehicleCategoryForm";
import {
  useCreateVehicleCategoryMutation,
  useGetVehicleCategoryById,
  useUpdateVehicleCategoryMutation,
} from "../../../../api/fleet-management/vehicle-category/vehicle-category.hooks";
import {
  buildVehicleCategoryPayload,
  initialVehicleCategoryForm,
  mapVehicleCategoryToForm,
  validateVehicleCategoryForm,
} from "./utils/vehicle-category.utils";

const VehicleCategoryFormPage = () => {
  const history = useHistory();
  const { vehicleCategoryId } = useParams();
  const isEditMode = Boolean(vehicleCategoryId);

  const [form, setForm] = useState(initialVehicleCategoryForm);
  const [initialFormState, setInitialFormState] = useState(initialVehicleCategoryForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const {
    data: categoryData,
    isLoading: isCategoryLoading,
    isError: isCategoryError,
    error: categoryError,
  } = useGetVehicleCategoryById(vehicleCategoryId);

  const {
    mutate: createVehicleCategory,
    isLoading,
    isPending,
    error,
    reset,
  } = useCreateVehicleCategoryMutation();

  const {
    mutate: updateVehicleCategory,
    isLoading: isUpdating,
    isPending: isUpdatePending,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateVehicleCategoryMutation();

  useEffect(() => {
    if (!isEditMode || !categoryData) return;
    const nextForm = mapVehicleCategoryToForm(categoryData);
    setForm(nextForm);
    setInitialFormState(nextForm);
  }, [categoryData, isEditMode]);

  const submitting = isLoading || isPending || isUpdating || isUpdatePending;
  const submitError = isEditMode ? updateError : error;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const nextErrors = validateVehicleCategoryForm(form);
    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    reset();
    resetUpdate();

    if (!validate()) return;

    const payload = buildVehicleCategoryPayload(form);

    if (isEditMode) {
      updateVehicleCategory(
        { categoryId: vehicleCategoryId, categoryData: payload },
        {
          onSuccess: () => {
            history.push("/fleet-management/vehicle-categories");
          },
        }
      );
      return;
    }

    createVehicleCategory(payload, {
      onSuccess: () => {
        history.push("/fleet-management/vehicle-categories");
      },
    });
  };

  if (isEditMode && isCategoryLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && isCategoryError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {categoryError?.response?.data?.message || "Failed to load vehicle category details."}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => history.push("/fleet-management/vehicle-categories")}
              aria-label="Back to vehicle categories list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">
              {isEditMode ? "Edit Vehicle Category" : "Create Vehicle Category"}
            </h4>
          </div>
          <span className="badge bg-info text-dark">Fleet Management</span>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            {isEditMode
              ? "Update vehicle category details."
              : "Fill in vehicle category details."}
          </p>

          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError?.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} vehicle category. Please try again.`}
            </Alert>
          )}

          <VehicleCategoryForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            isEditMode={isEditMode}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={() => {
              history.push("/fleet-management/vehicle-categories");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default VehicleCategoryFormPage;
